<template>
  <span
    :class="['relative inline-flex', wrapperClass]"
    @mouseenter="oeffneVerzoegert"
    @mouseleave="schliesse"
    @focusin="oeffneSofort"
    @focusout="schliesse"
  >
    <slot />

    <span
      v-if="offen && text"
      ref="kasten"
      role="tooltip"
      class="pointer-events-none z-50
             w-max max-w-[16rem] px-2 py-1 rounded-md shadow-lg
             bg-gray-900 text-white text-[11px] font-normal normal-case leading-snug
             text-left whitespace-pre-line"
      :class="lageKlassen"
      :style="fixiert ? stil : null"
    >
      {{ text }}
    </span>
  </span>
</template>

<script>
/**
 * Kleiner Tooltip im Stil der Oberflaeche.
 *
 * Warum nicht das native title-Attribut: es erscheint erst nach rund einer
 * Sekunde, laesst sich nicht gestalten, bricht keine Zeilen und verschwindet
 * beim Tastaturfokus gar nicht erst. Fuer eine Seite, deren Bedienung an
 * Tastenkuerzeln haengt, ist das zu wenig.
 *
 * Bewusst ohne Paket und ohne Teleport: der Tooltip liegt im Fluss seines
 * Ausloesers. Das genuegt ueberall dort, wo kein Vorfahre der betroffenen
 * Elemente seinen Ueberlauf beschneidet.
 *
 * ACHTUNG: 'absolut' reicht nicht in einem Scroll-Container. Setzt ein
 * Vorfahre overflow-x: auto, macht CSS aus dem noch 'visible' stehenden
 * overflow-y ebenfalls 'auto' -- der Container beschneidet dann auch nach
 * oben und unten, und ein Tooltip ueber der ersten Zeile ist weg. Dafuer ist
 * 'fixiert': der Tooltip haengt dann am Viewport statt am Ausloeser, seine
 * Lage wird beim Oeffnen aus dessen Rechteck berechnet.
 * ACHTUNG, die Grenze davon: position: fixed haengt am Viewport nur, solange
 * kein Vorfahre filter oder transform setzt -- beides erzeugt einen eigenen
 * Containing Block. Deshalb ist 'fixiert' ein Schalter und keine Vorgabe.
 */

// Halbe Maximalbreite (16rem) plus ein Rand von 8px. Damit laesst sich die
// Mitte so einspannen, dass auch ein voll ausgereizter Tooltip nicht ueber den
// Bildschirmrand laeuft -- ohne ihn dafuer erst rendern und messen zu muessen.
const HALBE_BREITE = 136;

export default {
  props: {
    text: { type: String, default: "" },
    /** Klassen fuer die Huelle -- noetig, wenn sie selbst ein Grid-Item ist. */
    wrapperClass: { type: String, default: "" },
    /** Standardmaessig ueber dem Element; unten, wo oben kein Platz ist. */
    oben: { type: Boolean, default: true },
    /** Am Viewport statt am Ausloeser -- noetig in Scroll-Containern. */
    fixiert: { type: Boolean, default: false },
  },
  data() {
    return { offen: false, timer: null, stil: null };
  },
  computed: {
    lageKlassen() {
      if (this.fixiert) return "fixed";
      return this.oben
        ? "absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5"
        : "absolute left-1/2 -translate-x-1/2 top-full mt-1.5";
    },
  },
  beforeUnmount() {
    this.entferneTimer();
    // Ohne das bliebe bei einem Unmount aus offenem Zustand ein Listener auf
    // window stehen, der in eine zerstoerte Komponente schreibt.
    this.beobachte(false);
  },
  methods: {
    // Kurze Verzoegerung, damit beim Ueberstreichen einer Knopfreihe nicht
    // reihum Tooltips aufblitzen.
    oeffneVerzoegert() {
      this.entferneTimer();
      this.timer = setTimeout(() => {
        this.oeffne();
        this.timer = null;
      }, 200);
    },
    // Per Tastatur sofort: wer hierher tabbt, will die Erklaerung jetzt.
    oeffneSofort() {
      this.entferneTimer();
      this.oeffne();
    },
    oeffne() {
      if (this.fixiert) {
        this.berechneLage();
        // Ein fixierter Tooltip kennt seinen Ausloeser nach dem Oeffnen nicht
        // mehr: scrollt die Seite, bliebe er stehen, wo er war. Statt ihn
        // mitzufuehren wird er geschlossen -- die Bewegung selbst ist das
        // Zeichen, dass der Nutzer gerade woanders hinsieht.
        this.beobachte(true);
      }
      this.offen = true;
      // ACHTUNG, warum das NACH dem Rendern noch einmal gerechnet wird:
      // berechneLage klemmt nur waagerecht. Senkrecht setzt es blind ueber
      // oder unter den Ausloeser -- ein hoher Kasten an einem Element nahe
      // der Fensterkante steht damit halb ausserhalb. Nachscrollen hilft
      // nicht, weil beobachte() ihn beim ersten Scrollereignis schliesst, und
      // pointer-events-none macht ihn ohnehin unerreichbar. Die Hoehe ist vor
      // dem Rendern nicht bekannt, also wird sie danach gemessen.
      if (this.fixiert) {
        this.$nextTick(this.spiegeleWennNoetig);
      }
    },
    schliesse() {
      this.entferneTimer();
      this.beobachte(false);
      this.offen = false;
    },
    berechneLage() {
      const r = this.$el.getBoundingClientRect();
      const breite = window.innerWidth;
      // Bei sehr schmalen Fenstern schluege das Einspannen um (die untere
      // Grenze laege ueber der oberen) und schoebe den Tooltip nach links
      // heraus. Dann bleibt die Mitte des Fensters die beste Wahl.
      const mitte =
        breite <= 2 * HALBE_BREITE
          ? breite / 2
          : Math.min(Math.max(r.left + r.width / 2, HALBE_BREITE), breite - HALBE_BREITE);
      this.stil = this.oben
        ? { left: mitte + "px", top: r.top - 6 + "px", transform: "translate(-50%, -100%)" }
        : { left: mitte + "px", top: r.bottom + 6 + "px", transform: "translate(-50%, 0)" };
      // Merken, in welche Richtung gerechnet wurde: spiegeleWennNoetig darf
      // sich nicht auf das Prop verlassen, sonst spiegelt es beim zweiten
      // Oeffnen zurueck.
      this._nachOben = this.oben;
    },

    /**
     * Senkrecht kippen, wenn der gerenderte Kasten sonst aus dem Fenster
     * ragt. Erst jetzt moeglich: vorher gab es keine Hoehe.
     *
     * Nur EINMAL kippen und danach hoechstens noch an die Kante schieben --
     * ein Kasten, der oben wie unten zu hoch ist (sehr kleines Fenster),
     * pendelte sonst zwischen beiden Seiten.
     */
    spiegeleWennNoetig() {
      const kasten = this.$refs.kasten;
      if (!kasten || !this.stil) return;
      const k = kasten.getBoundingClientRect();
      const hoehe = k.height;
      const platz = window.innerHeight;
      const RAND = 8;
      const r = this.$el.getBoundingClientRect();

      let nachOben = this._nachOben;
      if (nachOben && r.top - 6 - hoehe < RAND && r.bottom + 6 + hoehe <= platz - RAND) {
        nachOben = false;
      } else if (!nachOben && r.bottom + 6 + hoehe > platz - RAND && r.top - 6 - hoehe >= RAND) {
        nachOben = true;
      }

      // Oben wie unten zu wenig Platz: an die Kante setzen, statt halb
      // hinauszuragen. Lieber ein Kasten, der den Ausloeser ueberdeckt, als
      // einer, dessen zweite Haelfte niemand sieht.
      let top = nachOben ? r.top - 6 : r.bottom + 6;
      let transform = nachOben ? "translate(-50%, -100%)" : "translate(-50%, 0)";
      const obenkante = nachOben ? top - hoehe : top;
      if (obenkante < RAND) {
        top = RAND;
        transform = "translate(-50%, 0)";
      } else if (obenkante + hoehe > platz - RAND) {
        top = Math.max(RAND, platz - RAND - hoehe);
        transform = "translate(-50%, 0)";
      }

      this.stil = { ...this.stil, top: top + "px", transform };
    },
    // capture: true, weil ein Scroll in einem inneren Container nicht bis zu
    // window blubbert -- und genau die inneren Container sind der Grund, aus
    // dem es 'fixiert' ueberhaupt gibt.
    beobachte(an) {
      const m = an ? "addEventListener" : "removeEventListener";
      window[m]("scroll", this.schliesse, true);
      window[m]("resize", this.schliesse);
    },
    entferneTimer() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    },
  },
};
</script>
